import { type ReactNode, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom-v5-compat";
import { TRIPS_PATH } from "@b2bportal/core-utilities";
import { useSessionContext } from "@/components/AuthModule/SessionContext";
import {
  buildCommerceTripsUrl,
  hardNavigate,
  isCommerceTripsEnabled,
} from "@/utils/commerce/trips";

export interface TripsLinkProps {
  className?: string;
  children: ReactNode;
}

/**
 * My Trips link that crosses into commerce-portals with a full page load when
 * `/trips` is served there. A router `Link` would render the legacy Trips app.
 */
export const TripsLink = ({ className, children }: TripsLinkProps) => {
  const { sessionInfo } = useSessionContext();

  return isCommerceTripsEnabled() ? (
    <a
      className={className}
      href={buildCommerceTripsUrl({
        perkspotUserId: sessionInfo?.userDetails.tenantUserId,
        communityId: sessionInfo?.userDetails.communityId,
      })}
    >
      {children}
    </a>
  ) : (
    <Link className={className} to={TRIPS_PATH}>
      {children}
    </Link>
  );
};

export const useGoToTrips = (): (() => void) => {
  const navigate = useNavigate();
  const { sessionInfo } = useSessionContext();
  return useCallback(() => {
    if (isCommerceTripsEnabled()) {
      hardNavigate(
        buildCommerceTripsUrl({
          perkspotUserId: sessionInfo?.userDetails.tenantUserId,
          communityId: sessionInfo?.userDetails.communityId,
        })
      );
    } else {
      navigate(TRIPS_PATH);
    }
  }, [navigate, sessionInfo]);
};
